'use client'

import { useState, useEffect } from 'react'
import { useParams } from 'next/navigation'
import { useRouter } from '@/i18n/navigation'
import { Ticket, Send, CheckCircle, Mail, User as UserIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { ticketsAPI } from '@/lib/api/services/tickets'
import { useAuthStore } from '@/stores/auth'
import { useTicketGroups } from '@/hooks/useTickets'
import type { TicketPriority } from '@/types/ticket'

export default function PublicTicketCreatePage() {
  const router = useRouter()
  const params = useParams()
  const locale = params.locale as string
  const { toast } = useToast()
  const { publicUser, isUserAuthenticated } = useAuthStore()
  const { data: groups } = useTicketGroups()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [tokenUrl, setTokenUrl] = useState('')

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<TicketPriority>('medium')
  const [customerName, setCustomerName] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined)
  const [availableCategories, setAvailableCategories] = useState<any[]>([])

  // Auto-fill user info if logged in
  useEffect(() => {
    if (isUserAuthenticated && publicUser) {
      if (publicUser.email) {
        setCustomerEmail(publicUser.email)
      }
      if (publicUser.firstName || publicUser.lastName) {
        const name = [publicUser.firstName, publicUser.lastName]
          .filter(Boolean)
          .join(' ')
        setCustomerName(name)
      } else if (publicUser.username) {
        setCustomerName(publicUser.username)
      }
    }
  }, [isUserAuthenticated, publicUser])

  // Filter categories when group changes
  useEffect(() => {
    if (selectedGroupId && groups) {
      const group = groups.find((g: any) => g.id === selectedGroupId)
      if (group && group.categories) {
        setAvailableCategories(group.categories)
      } else {
        setAvailableCategories([])
      }
      setCategoryId(undefined)
    } else {
      setAvailableCategories([])
    }
  }, [selectedGroupId, groups])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!title.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a subject',
        variant: 'destructive',
      })
      return
    }

    if (!customerEmail.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter your email',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)

    try {
      const response = await ticketsAPI.public.create({
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        customerName: customerName.trim() || undefined,
        customerEmail: customerEmail.trim(),
        userId: isUserAuthenticated && publicUser ? publicUser.id : undefined,
        categoryId: categoryId || undefined,
      })

      if (!response.success) {
        throw new Error(response.error || 'Failed to create ticket')
      }

      setIsSuccess(true)

      toast({
        title: 'Success!',
        description: 'Your support ticket has been created',
      })

      // Redirect based on authentication status
      if (isUserAuthenticated && publicUser) {
        // Logged-in users: Go to My Tickets page
        setTimeout(() => {
          router.push('/my-tickets')
        }, 2000)
      } else {
        // Non-logged-in guests: Show token URL
        setTokenUrl(response.data!.token)
        // Token will be displayed in success message
      }
    } catch (error: any) {
      console.error('Ticket creation error:', error)
      toast({
        title: 'Error',
        description:
          error.message || 'Failed to create ticket. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-12 pb-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Ticket Created!
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Your support ticket has been submitted successfully. We'll get
                back to you soon.
              </p>
              {isUserAuthenticated && publicUser ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-xs font-medium text-blue-900 mb-2">
                    Redirecting to My Tickets...
                  </p>
                  <p className="text-xs text-blue-700">
                    You can view all your tickets in the My Tickets section
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-xs font-medium text-blue-900 mb-2">
                    Track Your Ticket
                  </p>
                  <p className="text-xs text-blue-700 mb-3">
                    Save this link to track your ticket (also sent to your
                    email):
                  </p>
                  <div className="bg-white rounded px-3 py-2 text-xs font-mono text-gray-800 break-all">
                    {window.location.origin}/{locale}/support/ticket/{tokenUrl}
                  </div>
                </div>
              )}
              <Button
                className="w-full"
                onClick={() =>
                  router.push(
                    isUserAuthenticated
                      ? '/my-tickets'
                      : `/support/ticket/${tokenUrl}`
                  )
                }
              >
                View My Ticket
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
            <Ticket className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Submit a Support Ticket
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Having an issue? Let us know and we'll get back to you as soon as
            possible.
          </p>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Ticket Details</CardTitle>
            <CardDescription>
              Please provide as much detail as possible to help us assist you
              better
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">
                  Your Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customerName">Name (Optional)</Label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="customerName"
                        placeholder="John Doe"
                        value={customerName}
                        onChange={e => setCustomerName(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerEmail">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="customerEmail"
                        type="email"
                        placeholder="john@example.com"
                        value={customerEmail}
                        onChange={e => setCustomerEmail(e.target.value)}
                        required
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Categorization */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">
                    Category (Optional)
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="group">Ticket Group</Label>
                    <Select
                      value={selectedGroupId?.toString() || ''}
                      onValueChange={value =>
                        setSelectedGroupId(value ? Number(value) : null)
                      }
                    >
                      <SelectTrigger id="group">
                        <SelectValue placeholder="Select a group (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {groups &&
                          groups.map((group: any) => (
                            <SelectItem
                              key={group.id}
                              value={group.id.toString()}
                            >
                              {group.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={categoryId?.toString() || ''}
                      onValueChange={value =>
                        setCategoryId(value ? Number(value) : undefined)
                      }
                      disabled={
                        !selectedGroupId || availableCategories.length === 0
                      }
                    >
                      <SelectTrigger id="category">
                        <SelectValue
                          placeholder={
                            selectedGroupId && availableCategories.length > 0
                              ? 'Select a category (optional)'
                              : 'Select a group first'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCategories.map((category: any) => (
                          <SelectItem
                            key={category.id}
                            value={category.id.toString()}
                          >
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Ticket Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">
                  Issue Details
                </h3>

                <div className="space-y-2">
                  <Label htmlFor="title">
                    Subject <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    placeholder="Brief description of your issue"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Please provide more details about your issue..."
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    minRows={6}
                  />
                  <p className="text-xs text-gray-500">
                    The more details you provide, the faster we can help resolve
                    your issue
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={priority}
                    onValueChange={value =>
                      setPriority(value as TicketPriority)
                    }
                  >
                    <SelectTrigger id="priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - General inquiry</SelectItem>
                      <SelectItem value="medium">
                        Medium - Standard issue
                      </SelectItem>
                      <SelectItem value="high">High - Urgent issue</SelectItem>
                      <SelectItem value="urgent">
                        Urgent - Critical problem
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Ticket
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                You'll receive a confirmation email with a link to track your
                ticket
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
